import {
  DocumentSnapshot,
  SnapshotOptions,
  Timestamp,
} from 'firebase/firestore';
import {
  CardContent,
  cardContentConverter,
  cardContentEqualsCardContent,
} from './card-content.model';
import { validateTimestamp } from './deck.model';

export type FlashcardId = string;

export class Flashcard {
  id: FlashcardId;
  deckId?: string;
  createdDate?: Timestamp;
  updatedDate?: Timestamp;
  verifyModeAnswerType?: 'input' | 'multiple-choice';
  isCaseSensitive?: boolean;
  numberOfTyposAllowed?: number;
  question?: CardContent;
  answers?: CardContent[]; // in document, not subcollection

  constructor(
    id: FlashcardId,
    deckId: string | undefined,
    createdDate: Timestamp | undefined,
    updatedDate: Timestamp | undefined,
    verifyModeAnswerType: 'input' | 'multiple-choice' | undefined,
    question: CardContent | undefined,
    answers: CardContent[] | undefined,
    isCaseSensitive = true,
    numberOfTyposAllowed = 0
  ) {
    this.id = id;
    this.deckId = deckId;
    this.createdDate = createdDate;
    this.updatedDate = updatedDate;
    this.verifyModeAnswerType = verifyModeAnswerType;
    this.question = question;
    this.answers = answers;
    this.isCaseSensitive = isCaseSensitive;
    this.numberOfTyposAllowed = numberOfTyposAllowed;
  }
}

export const flashcardEqualsFlashcard = (
  it: Flashcard | undefined,
  other: Flashcard | undefined
) => {
  if (it === undefined && other === undefined) {
    return true;
  } else if (it === undefined || other === undefined) {
    return false;
  }
  return (
    (it.deckId === other.deckId &&
      it.verifyModeAnswerType === other.verifyModeAnswerType &&
      it.isCaseSensitive === other.isCaseSensitive &&
      it.numberOfTyposAllowed === other.numberOfTyposAllowed &&
      cardContentEqualsCardContent(it.question, other.question) &&
      it.answers?.length === other.answers?.length &&
      it.answers?.every((a, i) =>
        cardContentEqualsCardContent(
          a,
          other.answers ? other.answers[i] : undefined
        )
      )) ||
    false
  );
};

export const flashcardConverter = {
  toFirestore: (flashcard: Flashcard) => {
    const obj: any = {
      deckId: flashcard.deckId,
      createdDate: validateTimestamp(flashcard.createdDate) || Timestamp.now(),
      updatedDate: validateTimestamp(flashcard.updatedDate) || Timestamp.now(),
      verifyModeAnswerType: flashcard.verifyModeAnswerType || null,
      question: cardContentConverter.toFirestore(flashcard.question) || null,
      isCaseSensitive: flashcard.isCaseSensitive,
      numberOfTyposAllowed: flashcard.numberOfTyposAllowed,
    };
    const answers = flashcard
      ? flashcard.answers
        ? flashcard.answers
            .filter((a) => a) // remove nulls
            .map((answer) => cardContentConverter.toFirestore(answer))
        : null
      : null;
    if (answers) {
      obj['answers'] = answers;
    }
    return obj;
  },
  fromFirestore: (snapshot: DocumentSnapshot, options?: SnapshotOptions) => {
    if (!snapshot.exists()) {
      return;
    }
    const data = snapshot.data(options);
    return new Flashcard(
      snapshot.id,
      data['deckId'] ?? undefined,
      data['createdDate'] ?? undefined,
      data['updatedDate'] ?? undefined,
      data['verifyModeAnswerType'] ?? undefined,
      cardContentConverter.fromFirestore2(data['question']),
      data['answers']?.map((answer: any) => {
        return cardContentConverter.fromFirestore2(answer);
      }) ?? undefined,
      data['isCaseSensitive'],
      data['numberOfTyposAllowed']
    );
  },
};
